import type { LinkProps } from 'next/link';
import React from 'react';

import { handleKeyboardClick } from '@/utils/accessibility';

import { Box } from '@/components/Box';
import { Typography } from '@/components/Typography';
import { generateDataTestId } from '@/lib/tests';

import * as S from './Stepper.styles';

interface StepperProps {
  activeStep: number;
  children?: React.ReactNode;
}

interface StepProps {
  children?: React.ReactNode;
  href?: LinkProps['href'];
  isActive?: boolean;
  isCompleted?: boolean;
  isDisabled?: boolean;
  isLast?: boolean;
  onEdit?: () => void;
  stepNumber?: number;
}

const Stepper = ({ activeStep, children }: StepperProps) => {
  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        isActive: index === activeStep,
        isCompleted: index < activeStep,
        isLast: index === React.Children.count(children) - 1,
        stepNumber: index + 1,
      } as React.PropsWithChildren<StepProps>);
    }
    return child;
  });
  return (
    <S.Wrapper
      {...generateDataTestId('stepper', 'wrapper')}
      data-test-id='StepperWrapper'
    >
      {childrenWithProps}
    </S.Wrapper>
  );
};

const Step = ({
  children,
  isActive = true,
  isCompleted = true,
  isDisabled = false,
  isLast = false,
  onEdit,
  stepNumber,
}: StepProps) => {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        stepsLength: React.Children.count(children),
      } as React.PropsWithChildren<StepProps>);
    }
    return child;
  });

  return (
    <S.StepContainer data-testid='StepperStep'>
      <Box>
        <S.StepBadge
          $isActive={isActive}
          $isCompleted={isCompleted}
          $isDisabled={isDisabled}
          data-testid={`StepperBadge${stepNumber}`}
          onClick={isCompleted ? onEdit : undefined}
          onKeyDown={isCompleted && onEdit ? handleKeyboardClick : undefined}
          role={isCompleted && onEdit ? 'button' : undefined}
          tabIndex={isCompleted && onEdit ? 0 : undefined}
        >
          {isCompleted ? (
            <S.CheckMark />
          ) : (
            <Typography
              color={isActive ? 'primary' : 'secondaryGrey'}
              fontFamily='italianPlate'
              fontWeight='bold'
              mt='s025'
              scale='p0'
            >
              {stepNumber}
            </Typography>
          )}
        </S.StepBadge>
        <Typography
          color={isActive || isCompleted ? 'primary' : 'secondaryGrey'}
          data-test-id='StepperTypography'
          mt='s025'
          position='relative'
          textAlign='center'
          textTransform='uppercase'
        >
          {childrenWithProps}
        </Typography>
      </Box>
      {!isLast && (
        <S.LineContainer>
          <S.StepLine isCompleted={isCompleted} />
        </S.LineContainer>
      )}
    </S.StepContainer>
  );
};

Stepper.Step = Step;

export { Stepper };
